import { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface AuthCardProps {
  title: string;
  subtitle?: ReactNode;
  /** Met en valeur le dernier mot du titre via gradient-text. */
  accentLastWord?: boolean;
  /** Slot au-dessus du formulaire (ex : badge référral, tabs Manager/Salarié). */
  topSlot?: ReactNode;
  /** Slot sous le formulaire (ex : « Pas de compte ? » + lien CGV). */
  bottomSlot?: ReactNode;
  children: ReactNode;
}

export function AuthCard({ title, subtitle, accentLastWord = true, topSlot, bottomSlot, children }: AuthCardProps) {
  const parts = title.split(" ");
  const head = parts.slice(0, -1).join(" ");
  const tail = parts.slice(-1).join(" ");

  return (
    <div className="animate-fade-in space-y-5">
      {/* Header */}
      <header className="px-1">
        <h1
          className={cn(
            "font-display italic font-medium tracking-tight leading-[1.1]",
            "text-[2rem] sm:text-4xl lg:text-[2.75rem]",
            "text-foreground",
          )}
        >
          {accentLastWord && parts.length > 1 ? (
            <>
              {head}{" "}
              <span className="gradient-text not-italic font-semibold">{tail}</span>
            </>
          ) : (
            <span className="gradient-text not-italic font-semibold">{title}</span>
          )}
        </h1>
        {subtitle && (
          <p className="mt-2.5 text-sm sm:text-base text-muted-foreground leading-relaxed font-sans">
            {subtitle}
          </p>
        )}
      </header>

      {/* Carte opaque : sur le fond blanc de la page, le flou d'arrière-plan ne se voyait pas
          mais se recalculait à chaque image du défilement (formulaire long sur mobile). */}
      <div
        className={cn(
          "relative rounded-2xl sm:rounded-3xl p-5 sm:p-6 lg:p-7",
          "bg-card",
          "border border-white/60 dark:border-white/10",
          "shadow-[0_20px_60px_-20px_hsl(var(--primary)/0.18),0_8px_24px_-8px_hsl(var(--foreground)/0.08)]",
          "before:absolute before:inset-0 before:rounded-[inherit] before:pointer-events-none",
          "before:shadow-[inset_0_1px_0_hsl(var(--background)/0.6)]",
        )}
      >
        {topSlot && <div className="mb-5">{topSlot}</div>}
        {children}
        {bottomSlot && (
          <div className="mt-6 pt-5 border-t border-border/50 text-center">{bottomSlot}</div>
        )}
      </div>
    </div>
  );
}
